import type { ToolbarProps } from 'react-big-calendar';
import { Navigate } from 'react-big-calendar';
import { useContext } from 'react';
import { useMediaQuery } from '@zg-rentals/react-hooks';
import { Box, Button, Heading, IconButton, Paragraph, TextButton } from '@zillow/constellation-10';
import {
  IconArrowLeftOutline,
  IconChevronLeftFilled,
  IconChevronRightFilled,
  IconPlusOutline,
  IconSettingsFilled,
} from '@zillow/constellation-icons';
import { differenceInCalendarWeeks, isSameDay, isSameWeek } from 'date-fns';

import {
  AVAILABILITY_LOOKAHEAD_WEEKS,
  getTourCalendarDescription,
  LG_BREAKPOINT,
  TOUR_CALENDAR_HEADER,
  TOUR_CALENDAR_MODES,
  type ZCalendarEvent,
} from '../../../../constants/TourCalendar';
import usePathsManifest from '../../../../hooks/usePathsManifest';

import TourCalendarContext from '../../../TourCalendarWithSettings/context/TourCalendarContext';

type TourCalendarToolbarProps = {
  mode: TOUR_CALENDAR_MODES;
  onClickSettings?: (shouldDisplaySettings: boolean) => void;
  onAddToYourAvailabilityClick: () => void;
  listingAlias: string;
  isInMobileWebView?: boolean;
  now: Date;
  lastActivatedDate: Date;
} & ToolbarProps<ZCalendarEvent>;
const TourCalendarToolbar = (props: TourCalendarToolbarProps) => {
  const { listingAddress } = useContext(TourCalendarContext);
  const { paths } = usePathsManifest('/rental-manager');
  const {
    mode,
    onClickSettings,
    onAddToYourAvailabilityClick,
    listingAlias,
    isInMobileWebView,
    now,
    lastActivatedDate,
    date: calendarDate,
  } = props;
  const isScreenLarge = useMediaQuery(`(min-width: ${LG_BREAKPOINT})`);
  const showBackNavigation = listingAddress && !isInMobileWebView && mode !== TOUR_CALENDAR_MODES.TOUR_AVAILABILITY;

  const onTodayClick = () => {
    props.onNavigate(Navigate.TODAY);
  };

  const onClickBack = () => {
    props.onNavigate(Navigate.PREVIOUS);
  };

  const onClickNext = () => {
    props.onNavigate(Navigate.NEXT);
  };

  if (isScreenLarge) {
    return (
      <Box
        css={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid {colors.legacy.coolGray.300}',
          justifyContent: 'space-between',
          marginBottom: 'tighter',
          padding: 'loose',
        }}
      >
        <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
          {showBackNavigation && (
            <IconButton
              data-testid="TourCalendarToolbar-HistoryBackBtn"
              asChild
              emphasis="bare"
              icon={<IconArrowLeftOutline />}
              title={`Back to ${listingAddress}`}
            >
              <a href={`${paths.propertiesPage}/${listingAlias}/dashboard/overview`} />
            </IconButton>
          )}
          <Button
            data-testid="TourCalendarToolbar-TodayBtn"
            css={{ marginLeft: 'default' }}
            onClick={onTodayClick}
            tone="neutral"
          >
            Today
          </Button>
          <Box css={{ marginLeft: 'default' }}>
            <IconButton
              data-testid="TourCalendarToolbar-BackBtn"
              disabled={isSameWeek(calendarDate, lastActivatedDate)}
              emphasis="bare"
              icon={<IconChevronLeftFilled />}
              onClick={onClickBack}
              title="Previous week"
            />
          </Box>
          <IconButton
            data-testid="TourCalendarToolbar-NextBtn"
            disabled={differenceInCalendarWeeks(calendarDate, now) >= AVAILABILITY_LOOKAHEAD_WEEKS}
            emphasis="bare"
            icon={<IconChevronRightFilled />}
            onClick={onClickNext}
            title="Next week"
          />
          <Box css={{ display: 'flex', flexDirection: 'column', marginLeft: 'default' }}>
            <Heading level={5}>{props.label}</Heading>
            {listingAddress && (
              <Paragraph
                data-testid="TourCalendarToolbar-ListingAddress"
                css={{ color: 'text.subtle' }}
                textStyle="body-xs"
              >
                {listingAddress}
              </Paragraph>
            )}
          </Box>
        </Box>
        <Box css={{ display: 'flex', alignItems: 'center', gap: 'loose', marginLeft: 'default' }}>
          {onClickSettings && (
            <Button
              data-testid="TourCalendarToolbar-SettingsBtn"
              icon={<IconSettingsFilled />}
              onClick={() => onClickSettings(true)}
              tone="brand"
              emphasis="outlined"
            >
              Settings
            </Button>
          )}
          <Button
            data-testid="TourCalendarToolbar-AddAvailabilityBtn"
            emphasis="filled"
            icon={<IconPlusOutline />}
            onClick={onAddToYourAvailabilityClick}
          >
            Add your availability
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: 'default',
        paddingLeft: 'default',
        paddingRight: 'default',
      }}
    >
      {mode === TOUR_CALENDAR_MODES.TOUR_AVAILABILITY && (
        <>
          <Heading
            data-testid="TourCalendarToolbar-Header"
            css={{ marginBottom: 'tighter' }}
            fontFamily="heading-alt"
            level={4}
          >
            {TOUR_CALENDAR_HEADER}
          </Heading>
          <Paragraph
            data-testid="TourCalendarToolbar-Description"
            color="text.subtle"
            css={{ marginBottom: 'default' }}
          >
            {getTourCalendarDescription(true)}
          </Paragraph>
        </>
      )}
      <>
        {showBackNavigation && (
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 'default',
              gap: 'tighter',
            }}
            data-testid="TourCalendarToolbar-HeaderRow-Mobile"
          >
            <TextButton
              data-testid="TourCalendarToolbar-HistoryBackBtn-Mobile"
              asChild
              icon={<IconChevronLeftFilled />}
              textStyle="body"
            >
              <a href={`${paths.propertiesPage}/${listingAlias}/dashboard/overview`}>Back</a>
            </TextButton>
            <Paragraph
              data-testid="TourCalendarToolbar-ListingAddress-Mobile"
              css={{
                flex: 1,
                textAlign: 'center',
              }}
              textStyle="body-bold"
            >
              {listingAddress}
            </Paragraph>
            <IconButton
              data-testid="TourCalendarToolbar-AddAvailabilityBtn-Mobile"
              aria-label="Add availability"
              emphasis="bare"
              icon={<IconPlusOutline />}
              onClick={onAddToYourAvailabilityClick}
              size="md"
              title="Add availability"
              tone="brand"
            />
          </Box>
        )}
        <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'default' }}>
          <Paragraph data-testid="TourCalendarToolbar-DayLabel-Mobile" textStyle="body-bold">
            {isSameDay(calendarDate, now) ? 'Today' : props.label}
          </Paragraph>
          <Box css={{ display: 'flex', gap: 'tighter' }}>
            <IconButton
              data-testid="TourCalendarToolbar-BackBtn-Mobile"
              disabled={isSameDay(calendarDate, lastActivatedDate)}
              emphasis="bare"
              icon={<IconChevronLeftFilled />}
              onClick={onClickBack}
              size="xs"
              title="Previous week"
            />
            <IconButton
              data-testid="TourCalendarToolbar-NextBtn-Mobile"
              disabled={differenceInCalendarWeeks(calendarDate, now) >= AVAILABILITY_LOOKAHEAD_WEEKS}
              emphasis="bare"
              icon={<IconChevronRightFilled />}
              onClick={onClickNext}
              size="xs"
              title="Next week"
            />
            {onClickSettings && (
              <Button
                data-testid="TourCalendarToolbar-SettingsBtn-Mobile"
                icon={<IconSettingsFilled />}
                onClick={() => onClickSettings(true)}
                tone="brand"
                emphasis="outlined"
              >
                Settings
              </Button>
            )}
          </Box>
        </Box>
      </>
    </Box>
  );
};

export default TourCalendarToolbar;
